import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { probeExampleConflicts } from '../../api/cafCorpus';
import { isMissingRoute } from './saveEngine';

/**
 * Final fix wave C1/C2: does this backend take the example "final response"
 * setting? The frontend can go live before the backend (or the backend can be
 * rolled back). An older backend refuses a correction preview that carries
 * `final_response_stance` and silently drops it from every other example save,
 * so on an older backend the setting is hidden and never required or sent, the
 * "Needs a final-response setting" pill and filter are hidden, and the conflict
 * check is skipped. On a backend that has it, nothing changes.
 *
 * The signal is `POST /caf-corpus/example-conflicts`, a route that ships in the
 * same backend deploy as the setting. One empty request per page load answers
 * it: only the app's global "no such route" 404 means an older backend
 * (`isMissingRoute`); any other answer, including a refusal, a 403 or no reply,
 * means the route exists or the deploy-order rule (backend first) holds. The
 * answer lives in this provider's React state: one page, one viewer, never a
 * module-level cache. Until it is in, nothing new is required or sent.
 *
 * With no provider above it (a form rendered on its own, as in its own tests),
 * the hook says supported: today's behaviour.
 */
type Answer = 'idle' | 'checking' | 'supported' | 'unsupported';

interface FinalResponseSupport {
  answer: Answer;
  /** Starts the one probe for this page; safe to call any number of times. */
  ask: () => void;
}

const FinalResponseSupportContext = createContext<FinalResponseSupport | null>(null);

export const FinalResponseSupportProvider: React.FC<{
  children: React.ReactNode;
  /** false: probe only once a form on the page needs the answer (e.g. the
   * Lender lane, where most viewers never teach the AI). */
  eager?: boolean;
}> = ({ children, eager = true }) => {
  const [answer, setAnswer] = useState<Answer>('idle');
  const asked = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const ask = useCallback(() => {
    if (asked.current) return;
    asked.current = true;
    setAnswer('checking');
    Promise.resolve()
      .then(() => probeExampleConflicts())
      .then(
        (): Answer => 'supported',
        (err: unknown): Answer => (isMissingRoute(err) ? 'unsupported' : 'supported'),
      )
      .then((next) => { if (mounted.current) setAnswer(next); });
  }, []);
  useEffect(() => { if (eager) ask(); }, [eager, ask]);
  const value = useMemo(() => ({ answer, ask }), [answer, ask]);
  return <FinalResponseSupportContext.Provider value={value}>{children}</FinalResponseSupportContext.Provider>;
};

/** True when the backend takes the final-response setting (see the file comment). */
export const useFinalResponseSupported = (): boolean => {
  const support = useContext(FinalResponseSupportContext);
  const ask = support?.ask;
  useEffect(() => { ask?.(); }, [ask]);
  return support === null || support.answer === 'supported';
};
